import { useEffect, useMemo, useState, useCallback } from "react";
import { startOfWeek, endOfWeek, parseISO, isWithinInterval, format } from "date-fns";
import { Loader2, Plus, Trash2, Gauge, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { getPillarById } from "@/data/pillars";

const inputCls =
  "w-full px-3 py-2 rounded-md border border-input bg-white/70 text-sm focus:outline-none focus:border-foreground/40 transition-colors duration-200";
const WORK_COLOR = getPillarById("work").color;

function fmtKm(n) {
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(n)} km`;
}

export default function TravelRegistration() {
  const [entries, setEntries] = useState(null);
  const [target, setTarget] = useState(0);
  const [km, setKm] = useState("");
  const [label, setLabel] = useState("");
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [e, s] = await Promise.all([api.get("/travel/entries"), api.get("/travel/settings")]);
      setEntries(e.data);
      setTarget(s.data.weekly_target_km || 0);
    } catch (err) {
      toast.error(formatApiError(err));
      setEntries([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const week = useMemo(() => {
    const now = new Date();
    return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
  }, []);

  const thisWeek = useMemo(
    () => (entries || []).filter((e) => isWithinInterval(parseISO(e.date), week)),
    [entries, week]
  );

  const weekTotal = useMemo(() => thisWeek.reduce((s, e) => s + e.km, 0), [thisWeek]);

  const byLabel = useMemo(() => {
    const m = {};
    thisWeek.forEach((e) => {
      const key = (e.label && e.label.trim()) || "Unlabeled";
      m[key] = (m[key] || 0) + e.km;
    });
    return m;
  }, [thisWeek]);

  const deviation = weekTotal - target;

  const submit = async (e) => {
    e.preventDefault();
    const n = parseFloat(km);
    if (!n || n <= 0) return toast.error("Enter a distance greater than 0");
    setBusy(true);
    try {
      await api.post("/travel/entries", { km: n, label: label.trim() || null, date, note: note.trim() || null });
      toast.success(`Logged ${fmtKm(n)}${label.trim() ? ` · ${label.trim()}` : ""}`);
      setKm("");
      setLabel("");
      setNote("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/travel/entries/${id}`);
      setEntries((es) => es.filter((x) => x.id !== id));
      toast.success("Entry removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (entries === null) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="travel-registration">
      {/* This week summary */}
      <div className="rounded-xl border border-border bg-card p-6" data-testid="travel-week-summary">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Gauge className="w-5 h-5 text-muted-foreground" strokeWidth={1.75} />
            <h2 className="font-serif text-xl text-foreground">This week</h2>
          </div>
          <div className="text-right">
            <p className="text-2xl font-medium text-foreground" data-testid="travel-week-total">{fmtKm(weekTotal)}</p>
            {target > 0 && (
              <p className={`text-xs flex items-center gap-1 justify-end ${deviation > 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid="travel-deviation">
                {deviation > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {Math.abs(Math.round(deviation))} km {deviation > 0 ? "over" : "under"} your {fmtKm(target)} target
              </p>
            )}
          </div>
        </div>
        {Object.keys(byLabel).length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No kilometers logged this week yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="travel-by-label">
            {Object.entries(byLabel)
              .sort((a, b) => b[1] - a[1])
              .map(([name, val]) => (
                <span key={name} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-border/70 bg-secondary/30">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: WORK_COLOR }} />
                  {name}: <strong className="font-medium">{fmtKm(val)}</strong>
                </span>
              ))}
          </div>
        )}
      </div>

      {/* Add form */}
      <form onSubmit={submit} className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="travel-add-form">
        <h2 className="font-serif text-xl text-foreground">Log kilometers</h2>
        <div className="grid sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Distance (km)</label>
            <input type="number" min="0" step="0.1" className={inputCls} value={km} onChange={(e) => setKm(e.target.value)} placeholder="e.g. 42" data-testid="travel-km-input" />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Label (optional)</label>
            <input className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Client visit" data-testid="travel-label-input" />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Date</label>
            <input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} data-testid="travel-date-input" />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Note (optional)</label>
            <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. client visit" data-testid="travel-note-input" />
          </div>
        </div>
        <button type="submit" disabled={busy} className="flex items-center gap-1.5 px-4 py-2.5 rounded-md font-medium text-background bg-foreground hover:bg-foreground/90 disabled:opacity-50 transition-colors duration-200" data-testid="travel-add-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add entry
        </button>
      </form>

      {/* Entries list */}
      <div>
        <h2 className="font-serif text-xl text-foreground mb-3">Recent entries</h2>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid="travel-entries-empty">No entries yet.</p>
        ) : (
          <div className="space-y-2" data-testid="travel-entries">
            {entries.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3" style={{ borderLeft: `3px solid ${WORK_COLOR}` }} data-testid={`travel-entry-${e.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    {fmtKm(e.km)}{e.label ? <> · <span className="text-foreground/70">{e.label}</span></> : ""}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(parseISO(e.date), "dd/MM/yyyy")}{e.note ? ` · ${e.note}` : ""}
                  </p>
                </div>
                <button onClick={() => remove(e.id)} className="p-2 text-muted-foreground hover:text-destructive shrink-0" data-testid={`travel-delete-${e.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
